// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import Edit from "@flip/XDL/Icons/Edit";
import { fontConfig, headingsFontConfig } from "@flip/XDL/PreStyle/typography";
import { color, iconLinkCommonStyles, mergeWithCss, xMask } from "@flip/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentShipToState, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface MiniSignInProps {
    linkGroups?: {
        title: string;
        links: (
            | {
                  title: string;
                  url: string;
                  type?: string | undefined;
                  id: string;
                  parentId?: string | undefined;
                  excludeFromNavigation?: boolean | undefined;
                  children?: PageLinkModel[] | undefined;
                  openInNewWindow?: boolean;
              }
            | undefined
        )[];
    }[];
    displayChangeCustomerLink?: boolean;
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

type Props = MiniSignInProps;

export interface MiniMyAccountStyles {
    wrapper?: InjectableCss;
    titleContainer?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    titleClickable?: ClickablePresentationProps;
    dashboardLink?: LinkPresentationProps;
    linkItems?: {
        wrapper?: InjectableCss;
        linkWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
    };
    personalInfo?: {
        wrapper?: InjectableCss;
        infoWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        subTitle?: TypographyPresentationProps;
        shipToInfo?: TypographyPresentationProps;
        link?: TypographyPresentationProps;
    };
    actionSection?: {
        wrapper?: InjectableCss;
        changeCustomerButton?: ButtonPresentationProps;
        signOutButton?: ButtonPresentationProps;
    };
}

const sharedSectionTitleStyle: TypographyPresentationProps = {
    variant: "h6",
    size: 14,
    css: css`
        flex-basis: 100%;
    `,
};

const miniMyAccountStyles: MiniMyAccountStyles = {
    wrapper: {
        css: css`
            min-width: 432px;
        `,
    },
    titleContainer: {
        css: css`
            padding: 0 16px;
            margin: 16px 0 12px 0;
        `,
    },
    titleWrapper: {
        css: css`
            align-items: center;
            display: flex;
            justify-content: space-between;
            padding-bottom: 12px;
            border-bottom: 1px solid ${color("common.ui14")};
        `,
    },
    title: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    dashboardLink: {
        typographyProps: {
            size: 12,
            lineHeight: "13px",
            weight: fontConfig.weight.semiBold,
        },
    },
    linkItems: {
        wrapper: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                padding: 0 16px;
                margin-bottom: 20px;
            `,
        },
        title: sharedSectionTitleStyle,
        link: {
            typographyProps: {
                size: 12,
                weight: fontConfig.weight.semiBold,
                lineHeight: "13px",
                css: css`
                    letter-spacing: 0;
                    margin-bottom: 6px;
                `,
            },
        },
        linkWrapper: {
            css: css`
                flex-basis: calc(50% - 16px);
            `,
        },
    },
    personalInfo: {
        wrapper: {
            css: css`
                margin-bottom: 18px;
                padding: 0 16px;
            `,
        },
        infoWrapper: {
            css: css``,
        },
        title: sharedSectionTitleStyle,
        subTitle: {
            size: 12,
            lineHeight: "17px",
            forwardAs: "h6",
            fontFamily: headingsFontConfig.fontFamily,
            weight: headingsFontConfig.weight.bold,
            css: css`
                flex-basis: 100%;
                margin-bottom: 9px;
            `,
        },
        shipToInfo: {
            size: 12,
            fontFamily: headingsFontConfig.fontFamily,
            ellipsis: true,
            lineHeight: "17px",
            css: css`
                display: block;
                width: 100%;
            `,
        },
        link: {
            ellipsis: true,
            lineHeight: "16px",
            color: "text.link",
            size: "12px",
            weight: 600,
            css: css`
                display: flex;
                margin-bottom: 5px;
                letter-spacing: 0;
                align-items: center;
                ${IconWrapper} {
                    margin-right: 4px;
                    opacity: 0.5;
                }
            `,
        },
    },
    actionSection: {
        wrapper: {
            css: css`
                border-top: 1px solid ${color("common.border")};
                padding: 16px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                flex-wrap: wrap;
                width: 100%;
            `,
        },
        changeCustomerButton: {
            variant: "secondary",
            sizeVariant: "medium",
            mergeCss: true,
            css: css``,
        },
    },
};

const MiniMyAccount: FC<Props> = ({ linkGroups, onSignOutHandler, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));
    const history = useHistory();

    const {
        currentBillTo,
        currentShipTo,
        displayChangeCustomerLink,
        isPunchOutSession,
        changeCustomerUrl,
        myAccountPageUrl,
    } = useAppSelector(state => {
        const session = getSession(state);
        return {
            currentBillTo: getCurrentBillToState(state).value,
            currentShipTo: getShipToState(state, session.shipToId).value,
            displayChangeCustomerLink: session.displayChangeCustomerLink,
            isPunchOutSession: getIsPunchOutSession(state),
            changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
            myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        };
    });

    const linksSection = linkGroups?.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper {...styles.linkItems?.wrapper} key={index}>
                    {linkGroup.title && <Typography {...styles.linkItems?.title}>{linkGroup.title}</Typography>}
                    {linkGroup.links.map(
                        link =>
                            link?.url && (
                                <StyledWrapper key={`${link?.title}_${link?.url}`} {...styles.linkItems?.linkWrapper}>
                                    <Link
                                        onClick={() => {
                                            hideAll();
                                        }}
                                        href={link?.url}
                                        {...styles.linkItems?.link}
                                        target={(link as any).openInNewWindow === true ? "_blank" : ""}
                                        aria-label={link?.title}
                                    >
                                        {link?.title}
                                    </Link>
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            );
        }
        return null;
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleContainer}>
                <StyledWrapper {...styles.titleWrapper}>
                    {currentBillTo && (
                        <Typography {...styles.title}>{`${translate("Customer #")} ${
                            currentBillTo?.customerNumber
                        }`}</Typography>
                    )}
                    <Link
                        onClick={() => {
                            hideAll();
                        }}
                        href={myAccountPageUrl}
                        aria-label={translate("View Dashboard")}
                        {...styles.dashboardLink}
                    >
                        {translate("View Dashboard")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>

            {linksSection}

            {currentShipTo && (
                <StyledWrapper {...styles.personalInfo?.wrapper}>
                    <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                        <Typography {...styles.personalInfo?.title}>{translate("Profile Information")}</Typography>
                        <Typography {...styles.personalInfo?.subTitle}>{translate("Shipping Address")}</Typography>
                        <Typography {...styles.personalInfo?.shipToInfo}>{`${currentShipTo.address1}`}</Typography>
                        <Typography {...styles.personalInfo?.shipToInfo}>{`${currentShipTo.address2}`}</Typography>
                        <Typography {...styles.personalInfo?.shipToInfo}>
                            {/* XNG-Changes: added a condition to check for state */}
                            {`${currentShipTo.city ? `${currentShipTo.city}, ` : ""} 
                               ${currentShipTo.state?.abbreviation ? `${currentShipTo.state?.abbreviation} ` : ""}
                             ${currentShipTo.postalCode}`}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.actionSection?.wrapper}>
                {displayChangeCustomerLink && (
                    <Link
                        href="#"
                        onClick={() => {
                            changeCustomerUrl && history.push(changeCustomerUrl);
                            hideAll();
                        }}
                        {...iconLinkCommonStyles(Edit)}
                        aria-label="Change Customer"
                    >
                        {translate("Change Ship-To Address")}
                    </Link>
                )}
                <Button
                    {...styles.actionSection?.signOutButton}
                    onClick={() => {
                        onSignOutHandler?.();
                        hideAll();
                    }}
                    aria-label="Sign Out"
                    data-test-selector="headerSignOutLink"
                    sizeVariant="small"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default MiniMyAccount;
